import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { formatMongoDate } from './dateUtils';

// Helper function to format dates consistently
const formatDate = (dateString) => {
  if (!dateString) return 'Date not available';
  
  try {
    const formattedDate = formatMongoDate(dateString);
    return formattedDate !== 'Invalid Date' ? formattedDate : 'Invalid date';
  } catch (err) {
    console.error("Date formatting error:", err, "for date:", dateString);
    return 'Invalid date';
  }
};

// Add error handling for missing dependencies
const checkDependencies = () => {
  if (typeof jsPDF === 'undefined') {
    throw new Error('jsPDF is not available. Please ensure jspdf is installed.');
  }
  if (typeof saveAs === 'undefined') {
    throw new Error('file-saver is not available. Please ensure file-saver is installed.');
  }
};

// PDF Export Functions - Updated to handle multiple tables for different data structures
export const exportToPDF = (data, columns, title, filename, visibleColumns = null, dataStructure = 'flat', tableConfigs = null) => {
  try {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 22);
    
    // Add timestamp
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);
    
    let currentY = 50;
    
    if (dataStructure === 'curriculum-separated') {
      // Handle curriculum-separated data structure for PDF
      Object.entries(data).forEach(([curriculumId, curriculumData], index) => {
        // Add curriculum header
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Curriculum: ${curriculumId}`, 14, currentY);
        currentY += 15;
        
        // Get table-specific configuration
        const tableConfig = tableConfigs && tableConfigs[curriculumId] ? tableConfigs[curriculumId] : null;
        const visibleColumnsForCurriculum = tableConfig ? tableConfig.visibleColumns : ['rotation', 'dateRange', 'students'];
        const sortConfig = tableConfig ? tableConfig.sortConfig : { column: null, direction: 'asc' };
        
        // Prepare table data for this curriculum
        let tableData = [];
        let processedData = curriculumData;
        
        // Apply sorting if specified
        if (Array.isArray(processedData) && sortConfig && sortConfig.column) {
          processedData = [...processedData].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.column) {
              case 'rotation':
                aValue = a.rotation !== undefined ? a.rotation : 0;
                bValue = b.rotation !== undefined ? b.rotation : 0;
                break;
              case 'dateRange':
                // Sort by start date
                const aDate = a.dates?.start || a.rotationStartDate || a.start_date || '';
                const bDate = b.dates?.start || b.rotationStartDate || b.start_date || '';
                aValue = new Date(aDate);
                bValue = new Date(bDate);
                break;
              case 'students':
                // Sort by number of students or first student name
                if (a.students && a.students.length > 0 && b.students && b.students.length > 0) {
                  aValue = (a.students[0].name || '').toLowerCase();
                  bValue = (b.students[0].name || '').toLowerCase();
                } else {
                  aValue = a.students ? a.students.length : 0;
                  bValue = b.students ? b.students.length : 0;
                }
                break;
              default:
                return 0;
            }
            
            if (sortConfig.direction === 'desc') {
              return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            } else {
              return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            }
          });
        }
        
        // Build dynamic header based on visible columns
        const headerColumns = [];
        if (visibleColumnsForCurriculum.includes('rotation')) {
          headerColumns.push('Rotation');
        }
        if (visibleColumnsForCurriculum.includes('dateRange')) {
          headerColumns.push('Date Range');
        }
        if (visibleColumnsForCurriculum.includes('students')) {
          headerColumns.push('Name', 'Email', 'My Role');
        }
        
        if (Array.isArray(processedData)) {
          processedData.forEach((rotation, rotationIndex) => {
            // Use the same date logic as the table rendering
            let dateRange = null;
            
            if (rotation.dates) {
              dateRange = {
                startDate: rotation.dates.start,
                endDate: rotation.dates.end
              };
            } else if (rotation.rotationStartDate && rotation.rotationEndDate) {
              dateRange = {
                startDate: rotation.rotationStartDate,
                endDate: rotation.rotationEndDate
              };
            } else if (rotation.start_date && rotation.end_date) {
              dateRange = {
                startDate: rotation.start_date,
                endDate: rotation.end_date
              };
            }
            
            const dateRangeString = dateRange ? 
              `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}` : 
              'Date range not available';
            
            const actualRotationIndex = rotation.rotation !== undefined ? rotation.rotation : rotationIndex;
            
            if (visibleColumnsForCurriculum.includes('students') && rotation.students && Array.isArray(rotation.students) && rotation.students.length > 0) {
              rotation.students.forEach(student => {
                const rowData = [];
                
                if (visibleColumnsForCurriculum.includes('rotation')) {
                  rowData.push(`Rotation ${actualRotationIndex + 1}`);
                }
                if (visibleColumnsForCurriculum.includes('dateRange')) {
                  rowData.push(dateRangeString);
                }
                if (visibleColumnsForCurriculum.includes('students')) {
                  rowData.push(
                    student.name || '',
                    student.email || '',
                    student.isCoprecepted ? 'Co-Preceptor' : 'Preceptor'
                  );
                }
                
                tableData.push(rowData);
              });
            } else if (visibleColumnsForCurriculum.includes('students')) {
              const rowData = [];
              
              if (visibleColumnsForCurriculum.includes('rotation')) {
                rowData.push(`Rotation ${actualRotationIndex + 1}`);
              }
              if (visibleColumnsForCurriculum.includes('dateRange')) {
                rowData.push(dateRangeString);
              }
              if (visibleColumnsForCurriculum.includes('students')) {
                rowData.push('', '', '');
              }
              
              tableData.push(rowData);
            }
          });
        }
        
        // Only create table if there's data to show
        if (tableData.length > 0) {
          // Create table for this curriculum
          autoTable(doc, {
            head: [headerColumns],
            body: tableData,
            startY: currentY,
            styles: {
              fontSize: 9,
              cellPadding: 3,
            },
            headStyles: {
              fillColor: [43, 70, 99],
              textColor: 255,
              fontStyle: 'bold',
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250],
            },
            margin: { top: 10, bottom: 10 },
          });
          
          // Update currentY for next table
          currentY = doc.lastAutoTable.finalY + 20;
        }
        
        // Add new page if needed (except for last curriculum)
        if (index < Object.keys(data).length - 1 && currentY > 250) {
          doc.addPage();
          currentY = 20;
        }
      });
    } else {
      // Handle flat data structure (original behavior)
      // Filter columns to only include visible ones if specified
      const filteredColumns = visibleColumns ? 
        columns.filter(col => visibleColumns.includes(col.key) || visibleColumns.includes(col.label)) : 
        columns;
      
      // Prepare table data - export exactly what's rendered
      const tableData = data.map(row => 
        filteredColumns.map(col => {
          const value = row[col.key];
          
          // Don't replace with N/A - export exactly what's in the data
          if (value === null || value === undefined || value === '') {
            return '';  // Empty string instead of N/A
          }
          
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          
          return String(value);
        })
      );
      
      // Create table using autoTable - use the imported autoTable function
      autoTable(doc, {
        head: [filteredColumns.map(col => col.label)],
        body: tableData,
        startY: currentY,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [43, 70, 99],
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250],
        },
        margin: { top: 40 },
      });
    }
    
    // Save the PDF
    doc.save(`${filename}.pdf`);
    
  } catch (error) {
    console.error('PDF export error:', error);
    throw new Error(`PDF export failed: ${error.message}`);
  }
};

// CSV Export Functions - Updated to export only visible data without N/A replacements
export const exportToCSV = (data, columns, filename, visibleColumns = null) => {
  // Filter columns to only include visible ones if specified
  const filteredColumns = visibleColumns ? 
    columns.filter(col => visibleColumns.includes(col.key) || visibleColumns.includes(col.label)) : 
    columns;
  
  // Create CSV header
  const header = filteredColumns.map(col => col.label).join(',');
  
  // Create CSV rows - export exactly what's rendered
  const rows = data.map(row => {
    const rowValues = filteredColumns.map(col => {
      const value = row[col.key];
      
      // Don't replace with N/A - export exactly what's in the data
      if (value === null || value === undefined || value === '') {
        return '';  // Empty string instead of N/A
      }
      
      if (Array.isArray(value)) {
        return `"${value.join(', ')}"`;
      }
      
      if (typeof value === 'object') {
        return `"${JSON.stringify(value)}"`;
      }
      
      // Handle string values - escape quotes and wrap in quotes if contains comma
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    });
    
    return rowValues.join(',');
  });
  
  // Combine header and rows
  const csvContent = [header, ...rows].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}.csv`);
};

// Data Transformers for different page types - Updated to remove N/A values
export const transformScheduleData = (scheduleData, type = 'admin') => {
  const columns = [
    { key: 'rotation', label: 'Rotation' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'endDate', label: 'End Date' },
    { key: 'preceptor', label: 'Preceptor' },
    { key: 'student', label: 'Student' },
    { key: 'speciality', label: 'Speciality' },
    { key: 'institution', label: 'Institution' },
    { key: 'year', label: 'Curriculum' },
  ];

  const transformedData = [];
  
  if (type === 'admin') {
    // Transform admin schedule data
    Object.entries(scheduleData).forEach(([year, yearData]) => {
      if (Array.isArray(yearData)) {
        yearData.forEach((rotation, index) => {
          if (rotation.students && Array.isArray(rotation.students)) {
            rotation.students.forEach(student => {
              transformedData.push({
                rotation: `Rotation ${index + 1}`,
                startDate: rotation.dates?.start || '',
                endDate: rotation.dates?.end || '',
                preceptor: student.preceptor_name || '',
                student: student.student_name || '',
                speciality: student.speciality || '',
                institution: student.institution || '',
                year: year,
              });
            });
          }
        });
      }
    });
  } else if (type === 'student') {
    // Transform student schedule data
    Object.entries(scheduleData).forEach(([year, yearData]) => {
      if (Array.isArray(yearData)) {
        yearData.forEach((rotation, index) => {
          transformedData.push({
            rotation: `Rotation ${index + 1}`,
            startDate: rotation.dates?.start || '',
            endDate: rotation.dates?.end || '',
            preceptor: rotation.preceptor_name || '',
            student: 'Current Student',
            speciality: rotation.speciality || '',
            institution: rotation.institution || '',
            year: year,
          });
        });
      }
    });
  } else if (type === 'preceptor') {
    // Transform preceptor schedule data
    Object.entries(scheduleData).forEach(([year, yearData]) => {
      if (Array.isArray(yearData)) {
        yearData.forEach((rotation, index) => {
          if (rotation.students && Array.isArray(rotation.students)) {
            rotation.students.forEach(student => {
              transformedData.push({
                rotation: `Rotation ${index + 1}`,
                startDate: rotation.dates?.start || '',
                endDate: rotation.dates?.end || '',
                preceptor: 'Current Preceptor',
                student: student.student_name || '',
                speciality: student.speciality || '',
                institution: student.institution || '',
                year: year,
              });
            });
          }
        });
      }
    });
  }

  return { columns, data: transformedData };
};

export const transformUserData = (userData, userType) => {
  const baseColumns = [
    { key: 'firstname', label: 'First Name' },
    { key: 'lastname', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
  ];

  let columns = [...baseColumns];
  
  if (userType === 'student') {
    columns.push(
      { key: 'rank', label: 'Rank' },
      { key: 'speciality', label: 'Speciality' },
      { key: 'institution', label: 'Institution' }
    );
  } else if (userType === 'preceptor') {
    columns.push(
      { key: 'rank', label: 'Rank' },
      { key: 'speciality', label: 'Speciality' },
      { key: 'institution', label: 'Institution' },
      { key: 'availability', label: 'Availability' }
    );
  } else if (userType === 'admin') {
    columns.push(
      { key: 'adminType', label: 'Admin Type' },
      { key: 'speciality', label: 'Speciality' },
      { key: 'institution', label: 'Institution' }
    );
  }

  const transformedData = userData.map(user => ({
    ...user,
    adminType: user.adminType || (user.isPreceptorAdmin ? 'Preceptor Admin' : 'Standalone Admin'),
  }));

  return { columns, data: transformedData };
};

export const transformResourceData = (resourceData) => {
  const columns = [
    { key: 'filename', label: 'Filename' },
    { key: 'description', label: 'Description' },
    { key: 'target_audience', label: 'Target Audience' },
    { key: 'rank', label: 'Rank' },
    { key: 'specialityTags', label: 'Speciality Tags' },
    { key: 'institutionTags', label: 'Institution Tags' },
    { key: 'uploader_name', label: 'Uploader' },
    { key: 'upload_date', label: 'Upload Date' },
    { key: 'content_type', label: 'Content Type' },
  ];

  const transformedData = resourceData.map(resource => ({
    ...resource,
    specialityTags: Array.isArray(resource.specialityTags) ? resource.specialityTags.join(', ') : resource.specialityTags || '',
    institutionTags: Array.isArray(resource.institutionTags) ? resource.institutionTags.join(', ') : resource.institutionTags || '',
    upload_date: resource.upload_date ? new Date(resource.upload_date).toLocaleString() : '',
  }));

  return { columns, data: transformedData };
};

// Helper function to export exactly what's rendered in a table
export const exportRenderedTableData = (tableData, columns, title, filename, format = 'CSV') => {
  try {
    checkDependencies();
  } catch (error) {
    console.error('Export dependency error:', error);
    throw new Error('Export functionality is not available. Please check your browser and dependencies.');
  }

  if (format.toUpperCase() === 'PDF') {
    // Use flat data structure for regular exportRenderedTableData calls
    exportToPDF(tableData, columns, title, filename, null, 'flat');
  } else if (format.toUpperCase() === 'CSV') {
    exportToCSV(tableData, columns, filename);
  } else {
    throw new Error(`Unsupported format: ${format}`);
  }
};

// Function to prepare export data configuration
export const prepareExportData = (data, columns, title, filename) => {
  return {
    data,
    columns,
    title,
    filename,
    exportToPDF: (visibleColumns = null) => exportToPDF(data, columns, title, filename, visibleColumns, 'flat'),
    exportToCSV: (visibleColumns = null) => exportToCSV(data, columns, filename, visibleColumns)
  };
};

// Main export function - Updated to handle visible columns and filtered data
export const exportData = (data, type, format, title, filename, options = {}) => {
  try {
    checkDependencies();
  } catch (error) {
    console.error('Export dependency error:', error);
    throw new Error('Export functionality is not available. Please check your browser and dependencies.');
  }

  const { visibleColumns, filteredData, customColumns } = options;
  let transformedData;
  
  // Use filtered data if provided, otherwise use original data
  const dataToProcess = filteredData || data;
  
  switch (type) {
    case 'schedule-admin':
      transformedData = transformScheduleData(dataToProcess, 'admin');
      break;
    case 'schedule-student':
      transformedData = transformScheduleData(dataToProcess, 'student');
      break;
    case 'schedule-preceptor':
      transformedData = transformScheduleData(dataToProcess, 'preceptor');
      break;
    case 'users':
      transformedData = transformUserData(dataToProcess.users || dataToProcess, dataToProcess.userType || type);
      break;
    case 'resources':
      transformedData = transformResourceData(dataToProcess);
      break;
    case 'custom':
      // For custom data with custom columns
      transformedData = {
        data: dataToProcess,
        columns: customColumns || []
      };
      break;
    default:
      throw new Error(`Unknown export type: ${type}`);
  }

  if (format.toUpperCase() === 'PDF') {
    exportToPDF(transformedData.data, transformedData.columns, title, filename, visibleColumns, 'flat');
  } else if (format.toUpperCase() === 'CSV') {
    exportToCSV(transformedData.data, transformedData.columns, filename, visibleColumns);
  } else {
    throw new Error(`Unsupported format: ${format}`);
  }
}; 